import { apiClient } from './api-client';
import { ApiResponse } from '@/types/api-response';
import { mutate } from 'swr';
import i18n from '@/i18n/i18n';
import { toast } from '@/hooks/use-toast';

type RestoredItem = { type: 'product' | 'variant'; id: string; productId: string };
const restoreOffers = new Map<string, Promise<RestoredItem | null>>();

export async function restoreCatalogItem(type: 'product' | 'variant', id: string, notify = true) {
  const base = type === 'product' ? '/products' : '/product-variants';
  const response = await apiClient.post(`${base}/${id}/restore`, {});
  if (!response.success) throw new Error(response.message || 'Could not restore item');
  if (notify) toast({ title: i18n.t(type === 'product' ? 'product:recovery.productRestored' : 'product:recovery.variantRestored') });
  await mutate((key) => typeof key === 'string' &&
    (key.includes('/products') || key.includes('/product-variants')));
}

export async function offerCatalogRestore(response: ApiResponse<unknown>, notify = true) {
  const details = response.details;
  if (response.success || !details || Array.isArray(details) ||
    (details.type !== 'product' && details.type !== 'variant') || !details.id || !details.productId) return null;
  const item: RestoredItem = { type: details.type, id: details.id, productId: details.productId };
  const key = `${item.type}:${item.id}`;
  const existing = restoreOffers.get(key);
  if (existing) return existing;
  // Record the offer before opening a dialog, which can trigger more focus events.
  const offer = Promise.resolve().then(async () => {
    const confirmation = i18n.t(item.type === 'product' ? 'product:recovery.confirm' : 'product:recovery.confirmVariant');
    if (!window.confirm(`${response.message}\n\n${confirmation}`)) return null;
    await restoreCatalogItem(item.type, item.id, notify);
    return item;
  });
  restoreOffers.set(key, offer);
  try {
    return await offer;
  } catch (error) {
    restoreOffers.delete(key);
    throw error;
  }
}
